<template>
  <div class="form-section group">
    <span class="icon"><icon-colored-deal /></span>
    {{ capitalize(i18n.t('names.targetPublisherDeals')) }}
  </div>
  <div class="form-section-content">
    <div class="form-group row m-1">
      <!-- For the time being only one external SSP can be added to a CD -->
      <external-deals-selector
        :ssps="externalSSPsWrapped"
        :entity="deal"
        :use-media-owners="false"
        :can-add-rows="false"
        :add-row-warning-message="addRowWarningMessage"
        @ssps-changed="updatePackageMediaOwners" />
    </div>
  </div>
</template>

<script setup lang="ts">
  import { capitalize, computed, onBeforeMount, Ref } from 'vue';
  import { useI18n } from 'vue-i18n';

  import { IconColoredDeal } from '@/common/icons/colored';
  import { Package } from '@/common/models';
  import { useInjected } from '@/globals';
  import ExternalDealsSelector from '@/org-app/components/lineItems/form/deals/ExternalDealsSelector.vue';
  import { useMediaOwners } from '@/org-app/services/tanstack/queries';
  import { waitForPendingQueries } from '@/org-app/services/tanstack/queryUtils';
  import { mapActions } from '@/utils/StateMapping';

  // #region Props

  interface IProps {
    externalSSPs: Array<{ name: string; id: number }>;
  }

  const { externalSSPs = [] } = defineProps<IProps>();

  const deal: Ref<Package> = useInjected<Package>('deal');

  // #endregion

  // #region Data

  const { updatePackageMediaOwnerIDs } = mapActions();
  const i18n = useI18n();

  const mediaOwnersQuery = useMediaOwners();

  // #endregion

  // #region Computed

  const mediaOwners = computed(() => mediaOwnersQuery.items.value);
  const externalSSPsWrapped = computed(() => externalSSPs.map((ssp) => ({ name: ssp.name, id: ssp.id })));

  // Temporary until we support multiple eSSPs on a curated deal.
  const addRowWarningMessage = computed(() => i18n.t('messages.dealExternalSSPWarning'));

  // #endregion

  // #region Methods

  function updatePackageMediaOwners(sspIDs: number[]) {
    const mediaOwnerIDsMatchingSSPs = mediaOwners.value.filter((mo) => sspIDs.includes(mo.sspID)).map((mo) => mo.id);
    updatePackageMediaOwnerIDs({
      package: deal.value,
      value: mediaOwnerIDsMatchingSSPs,
    });
  }

  // #endregion

  // #region Lifecycle

  onBeforeMount(async () => {
    await waitForPendingQueries([mediaOwnersQuery]);
  });

  // #endregion
</script>

<style scoped lang="scss">
  @use '@/styles/variables.scss' as *;

  ::v-deep(#external_deal_buying_table) {
    .ssp-selection,
    .btn-add-ssp-deal {
      padding-left: 0;
    }
    .btn-remove-ssp-deal {
      padding-right: 0;
    }
  }
</style>
